// Cache bust: 1775098844
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, TrendingUp, Globe, Target, Crown, Heart,
  BarChart3, PieChart, Activity, ArrowLeft, RefreshCw,
  CheckCircle, ListTodo, MessageSquare, Trophy, Calendar,
  Mail, Send, Clock, Trash2, Shield, AlertTriangle,
  Search, Eye, Edit, UserCog, CreditCard, History, X, Save,
  UserPlus, ShieldCheck, ShieldOff, DollarSign, Zap, Percent,
  TrendingDown, Award, FlaskConical, ArrowUpRight, Share2, Flame,
  Gift, RotateCcw, Bell, UserCheck, Link2, Wrench, ServerCrash, Brain
} from "lucide-react";
import AdminUserDataPanel from "@/components/AdminUserDataPanel";
import AdminLiveOpsTab from "@/components/admin/AdminLiveOpsTab";
import AdminReliabilityTab from "@/components/admin/AdminReliabilityTab";
import AdminFeatureFlagsTab from "@/components/admin/AdminFeatureFlagsTab";
import AdminAIInsightsTab from "@/components/admin/AdminAIInsightsTab";

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [itemAnalytics, setItemAnalytics] = useState(null);
  const [eventAnalytics, setEventAnalytics] = useState(null);
  const [countryAnalytics, setCountryAnalytics] = useState(null);
  const [funnelAnalytics, setFunnelAnalytics] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [timeRange, setTimeRange] = useState("30");
  const [refreshing, setRefreshing] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testEmailType, setTestEmailType] = useState("welcome");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [checkingInactive, setCheckingInactive] = useState(false);
  
  // Cleanup state
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [loadingCleanup, setLoadingCleanup] = useState(false);
  const [executingCleanup, setExecutingCleanup] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());

  // Subscription Enforcement state
  const [enforcementEnabled, setEnforcementEnabled] = useState(true);
  const [enforcementLoading, setEnforcementLoading] = useState(false);
  const [enforcementUpdatedBy, setEnforcementUpdatedBy] = useState(null);

  // User Management state
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [userList, setUserList] = useState([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [userListTotal, setUserListTotal] = useState(0);
  const [userListOffset, setUserListOffset] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(false);
  const [userEditForm, setUserEditForm] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  const [adminLogs, setAdminLogs] = useState([]);
  
  // Create User state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    email: "",
    password: "",
    name: "",
    is_admin: false,
    founding_explorer: false
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Admin Toggle state
  const [togglingAdmin, setTogglingAdmin] = useState(null);

  // Monetization Analytics state
  const [monetizationOverview, setMonetizationOverview] = useState(null);
  const [paywallPerformance, setPaywallPerformance] = useState(null);
  const [experimentResults, setExperimentResults] = useState(null);
  const [dailyRevenue, setDailyRevenue] = useState(null);
  const [topTriggers, setTopTriggers] = useState(null);
  const [variantLeaderboard, setVariantLeaderboard] = useState(null);
  const [monetizationLoading, setMonetizationLoading] = useState(false);

  // Growth Loops Analytics state
  const [growthOverview, setGrowthOverview] = useState(null);
  const [growthLoading, setGrowthLoading] = useState(false);

  const fetchAllAnalytics = async () => {
    setRefreshing(true);
    try {
      const [overviewRes, usersRes, itemsRes, eventsRes, countriesRes, funnelRes, emailRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`),
        axios.get(`${API}/admin/analytics/users?days=${timeRange}`),
        axios.get(`${API}/admin/analytics/items?days=${timeRange}`),
        axios.get(`${API}/admin/analytics/events?days=7`),
        axios.get(`${API}/admin/analytics/countries`),
        axios.get(`${API}/admin/analytics/funnel`),
        axios.get(`${API}/admin/email/stats`),
      ]);

      setOverview(overviewRes.data);
      setUserAnalytics(usersRes.data);
      setItemAnalytics(itemsRes.data);
      setEventAnalytics(eventsRes.data);
      setCountryAnalytics(countriesRes.data);
      setFunnelAnalytics(funnelRes.data);
      setEmailStats(emailRes.data);
      setUserAnalytics(usersRes.data);
      setItemAnalytics(itemsRes.data);
      setEventAnalytics(eventsRes.data);
      setCountryAnalytics(countriesRes.data);
      setFunnelAnalytics(funnelRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllAnalytics();
    fetchEnforcementStatus();
  }, [timeRange]);

  const fetchMonetizationData = async () => {
    setMonetizationLoading(true);
    try {
      const [overviewRes, paywallRes, expRes, revenueRes, triggersRes, variantsRes] = await Promise.all([
        axios.get(`${API}/admin/monetization/overview?days=${timeRange}`),
        axios.get(`${API}/admin/monetization/paywall-performance?days=${timeRange}`),
        axios.get(`${API}/admin/monetization/experiments?days=${timeRange}`),
        axios.get(`${API}/admin/monetization/daily-revenue?days=${timeRange}`),
        axios.get(`${API}/admin/monetization/top-triggers?days=${timeRange}`),
        axios.get(`${API}/admin/monetization/variant-leaderboard?days=${timeRange}`),
      ]);
      
      setMonetizationOverview(overviewRes.data);
      setPaywallPerformance(paywallRes.data);
      setExperimentResults(expRes.data);
      setDailyRevenue(revenueRes.data);
      setTopTriggers(triggersRes.data);
      setVariantLeaderboard(variantsRes.data);
    } catch (error) {
      console.error("Failed to load monetization data:", error);
      toast.error("Failed to load monetization data");
    } finally {
      setMonetizationLoading(false);
    }
  };

  const fetchGrowthData = async () => {
    setGrowthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/growth/overview?days=${timeRange}`);
      setGrowthOverview(res.data);
    } catch (error) {
      console.error("Failed to load growth data:", error);
      toast.error("Failed to load growth data");
    } finally {
      setGrowthLoading(false);
    }
  };

  const fetchEnforcementStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/enforcement`);
      setEnforcementEnabled(res.data.enabled);
      setEnforcementUpdatedBy(res.data.updated_by);
    } catch (error) {
      console.error("Failed to fetch enforcement status");
    }
  };

  const toggleEnforcement = async () => {
    setEnforcementLoading(true);
    try {
      const res = await axios.post(`${API}/admin/settings/enforcement`, {
        enabled: !enforcementEnabled
      });
      setEnforcementEnabled(res.data.enabled);
      toast.success(res.data.message);
    } catch (error) {
      toast.error("Failed to update enforcement setting");
    } finally {
      setEnforcementLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter an email address");
      return;
    }
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/admin/email/test`, {
        email: testEmail,
        email_type: testEmailType,
      });
      if (res.data.status === "success") {
        toast.success("Test email sent!");
      } else if (res.data.status === "skipped") {
        toast.warning(`Email skipped: ${res.data.reason}`);
      } else {
        toast.error(`Failed: ${res.data.error}`);
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleProcessQueue = async () => {
    setProcessingQueue(true);
    try {
      const res = await axios.post(`${API}/admin/email/process-queue`);
      toast.success(`Processed ${res.data.processed} emails`);
      fetchAllAnalytics();
    } catch (error) {
      toast.error("Failed to process queue");
    } finally {
      setProcessingQueue(false);
    }
  };

  const handleCheckInactive = async () => {
    setCheckingInactive(true);
    try {
      const res = await axios.post(`${API}/admin/email/check-inactive`);
      toast.success(`Checked ${res.data.checked} users, sent ${res.data.emails_sent} emails`);
      fetchAllAnalytics();
    } catch (error) {
      toast.error("Failed to check inactive users");
    } finally {
      setCheckingInactive(false);
    }
  };

  // Cleanup functions
  const handlePreviewCleanup = async () => {
    setLoadingCleanup(true);
    setSelectedForDeletion(new Set());
    try {
      const res = await axios.get(`${API}/admin/cleanup/preview`);
      setCleanupPreview(res.data);
      // Pre-select all test accounts
      const testIds = res.data.test_accounts.map(u => u.id);
      setSelectedForDeletion(new Set(testIds));
    } catch (error) {
      toast.error("Failed to load cleanup preview");
    } finally {
      setLoadingCleanup(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedForDeletion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const selectAllTestAccounts = () => {
    if (cleanupPreview) {
      const testIds = cleanupPreview.test_accounts.map(u => u.id);
      setSelectedForDeletion(new Set(testIds));
    }
  };

  const deselectAll = () => {
    setSelectedForDeletion(new Set());
  };

  const handleExecuteCleanup = async () => {
    if (selectedForDeletion.size === 0) {
      toast.error("No users selected for deletion");
      return;
    }
    
    if (!window.confirm(`⚠️ Are you sure you want to delete ${selectedForDeletion.size} selected accounts? This cannot be undone!`)) {
      return;
    }
    
    setExecutingCleanup(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/execute-selected`, {
        user_ids: Array.from(selectedForDeletion)
      });
      toast.success(res.data.message);
      setCleanupPreview(null);
      setSelectedForDeletion(new Set());
      fetchAllAnalytics(); // Refresh stats
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to execute cleanup");
    } finally {
      setExecutingCleanup(false);
    }
  };

  // Toggle per-user protected flag — used by the Protect switch on each
  // row in the cleanup preview. Optimistic UI: flip in local state first,
  // revert on error. Keeps the cleanup view interactive without a full
  // reload after every toggle.
  const toggleProtection = async (user, nextProtected) => {
    setCleanupPreview((prev) => {
      if (!prev) return prev;
      const flipUser = (u) => u.id === user.id ? { ...u, protected: nextProtected, protection_source: nextProtected ? "explicit" : null } : u;
      let real = prev.real_accounts.map(flipUser);
      let test = prev.test_accounts.map(flipUser);
      // Re-classify: protected → real_accounts, unprotected → test_accounts (only if email looks like test)
      const moved = (nextProtected ? test : real).find((u) => u.id === user.id);
      if (moved) {
        if (nextProtected) {
          real = [...real, moved];
          test = test.filter((u) => u.id !== user.id);
        } else {
          // Always move to test_accounts on unprotect — if email is real-looking,
          // a reload via "Refresh preview" will reclassify back.
          test = [...test, moved];
          real = real.filter((u) => u.id !== user.id);
        }
      }
      return {
        ...prev,
        real_accounts: real, test_accounts: test,
        real_accounts_count: real.length, test_accounts_count: test.length,
      };
    });
    // Drop from delete-set if just got protected
    if (nextProtected) {
      setSelectedForDeletion((s) => { const n = new Set(s); n.delete(user.id); return n; });
    }
    try {
      await axios.post(`${API}/admin/users/${user.id}/protection`, { protected: nextProtected });
      toast.success(`${nextProtected ? "Protected" : "Unprotected"}: ${user.name || user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update protection");
      // Reload to resync from server on failure
      try {
        const r = await axios.get(`${API}/admin/cleanup/preview`);
        setCleanupPreview(r.data);
      } catch { /* swallow */ }
    }
  };

  // ==================== USER MANAGEMENT FUNCTIONS ====================

  // Pagination state — admin can step through pages of users instead of
  // being silently capped at 50 (which previously hid 7 of your users).
  // 200 is the new per-page batch; total is returned by the backend so
  // we can show "X of Y" and a Load more button when needed.
  const USER_PAGE_SIZE = 200;

  const searchUsers = async (offsetOverride = 0, append = false) => {
    setUserListLoading(true);
    try {
      const params = new URLSearchParams();
      if (userSearch) params.append("search", userSearch);
      if (userFilter && userFilter !== "all") params.append("filter_type", userFilter);
      params.append("limit", String(USER_PAGE_SIZE));
      params.append("offset", String(offsetOverride));

      const res = await axios.get(`${API}/admin/users?${params.toString()}`);
      const incoming = res.data.users || [];
      if (append) {
        setUserList((prev) => [...prev, ...incoming]);
      } else {
        setUserList(incoming);
      }
      setUserListTotal(res.data.total ?? incoming.length);
      setUserListOffset(offsetOverride + incoming.length);
    } catch (error) {
      toast.error("Failed to search users");
    } finally {
      setUserListLoading(false);
    }
  };

  const loadMoreUsers = () => searchUsers(userListOffset, true);

  const loadUserDetails = async (userId) => {
    setUserDetailsLoading(true);
    setSelectedUser(userId);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setUserDetails(res.data);
      setUserEditForm({
        name: res.data.user.name,
        is_premium: res.data.subscriptions?.some(s => s.status === "active") || false,
        founding_explorer: res.data.user.founding_explorer || false,
        founding_explorer_number: res.data.user.founding_explorer_number || "",
        admin_note: res.data.user.admin_note || ""
      });
    } catch (error) {
      toast.error("Failed to load user details");
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const saveUserChanges = async () => {
    if (!selectedUser) return;
    setSavingUser(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser}`, userEditForm);
      toast.success("User updated successfully");
      setEditingUser(false);
      loadUserDetails(selectedUser); // Refresh
      searchUsers(); // Refresh list
    } catch (error) {
      toast.error("Failed to save user changes");
    } finally {
      setSavingUser(false);
    }
  };

  const updateSubscription = async (status) => {
    if (!selectedUser) return;
    try {
      await axios.post(`${API}/admin/users/${selectedUser}/subscription`, {
        status,
        plan: status === "active" ? "admin_granted" : null
      });
      toast.success(`Subscription ${status}`);
      loadUserDetails(selectedUser);
      searchUsers();
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const deleteUserAccount = async () => {
    if (!selectedUser || !userDetails) return;
    
    if (!window.confirm(`⚠️ DELETE USER: ${userDetails.user.name} (${userDetails.user.email})?\n\nThis will permanently delete:\n- ${userDetails.item_stats.total} bucket items\n- All photos and comments\n- All achievements\n\nThis cannot be undone!`)) {
      return;
    }
    
    try {
      const res = await axios.delete(`${API}/admin/users/${selectedUser}`);
      toast.success(res.data.message);
      setSelectedUser(null);
      setUserDetails(null);
      searchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const loadAdminLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/logs?limit=50`);
      setAdminLogs(res.data.logs);
    } catch (error) {
      console.error("Failed to load admin logs");
    }
  };

  // Create new user function
  const handleCreateUser = async () => {
    if (!createUserForm.email || !createUserForm.password || !createUserForm.name) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setCreatingUser(true);
    try {
      const res = await axios.post(`${API}/admin/users/create`, createUserForm);
      toast.success(`User ${res.data.user.name} created successfully!`);
      setShowCreateUserModal(false);
      setCreateUserForm({
        email: "",
        password: "",
        name: "",
        is_admin: false,
        founding_explorer: false
      });
      searchUsers(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  // Toggle admin status function
  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    setTogglingAdmin(userId);
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/toggle-admin`, {
        is_admin: !currentIsAdmin
      });
      toast.success(res.data.message);
      
      // Update the user list
      setUserList(prev => prev.map(u => 
        u.id === userId ? { ...u, is_admin: !currentIsAdmin } : u
      ));
      
      // Update user details if viewing this user
      if (userDetails && userDetails.user.id === userId) {
        setUserDetails(prev => ({
          ...prev,
          user: { ...prev.user, is_admin: !currentIsAdmin }
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle admin status");
    } finally {
      setTogglingAdmin(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-lg">Loading analytics...</div>
      </div>
    );
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "primary", trend }) => (
    <Card className="border-none shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className="text-3xl font-bold text-foreground mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend && (
              <p className={`text-xs mt-2 ${trend > 0 ? 'text-green-600' : 'text-red-500'}`}>
                {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last period
              </p>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-${color}/10`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SimpleBarChart = ({ data, labelKey, valueKey, color = "#E07A5F" }) => {
    if (!data || data.length === 0) return <p className="text-muted-foreground text-sm">No data available</p>;
    const maxValue = Math.max(...data.map(d => d[valueKey]));
    
    return (
      <div className="space-y-2">
        {data.slice(0, 10).map((item) => (
          <div key={item[labelKey] || item.id || `${item[labelKey]}-${item[valueKey]}`} className="flex items-center gap-3">
            <div className="w-24 text-xs text-muted-foreground truncate">{item[labelKey]}</div>
            <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${(item[valueKey] / maxValue) * 100}%`,
                  backgroundColor: color 
                }}
              />
            </div>
            <div className="w-12 text-sm font-medium text-foreground text-right">{item[valueKey]}</div>
          </div>
        ))}
      </div>
    );
  };

  const FunnelChart = ({ data }) => {
    if (!data || data.length === 0) return null;
    
    return (
      <div className="space-y-3">
        {data.map((stage) => (
          <div key={stage.name || stage.stage} className="relative">
            <div 
              className="h-12 rounded-lg flex items-center justify-between px-4 transition-all"
              style={{ 
                width: `${Math.max(stage.percentage, 20)}%`,
                backgroundColor: `hsl(${180 - data.indexOf(stage) * 30}, 60%, ${75 - data.indexOf(stage) * 8}%)`,
                marginLeft: `${(100 - Math.max(stage.percentage, 20)) / 2}%`
              }}
            >
              <span className="font-medium text-slate-800 text-sm">{stage.stage}</span>
              <span className="font-bold text-slate-800">{stage.users}</span>
            </div>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              {stage.percentage}%
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background overflow-y-auto pb-20">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-serif font-bold text-foreground flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-primary" />
                  Analytics Dashboard
                </h1>
                <p className="text-sm text-muted-foreground">
                  Last updated: {overview?.generated_at ? new Date(overview.generated_at).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={fetchAllAnalytics}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Users"
            value={overview?.users?.total || 0}
            subtitle={`+${overview?.users?.this_week || 0} this week`}
            icon={Users}
            color="primary"
          />
          <StatCard
            title="Bucket List Items"
            value={overview?.items?.total || 0}
            subtitle={`${overview?.items?.completion_rate || 0}% completed`}
            icon={ListTodo}
            color="secondary"
          />
          <StatCard
            title="Countries Explored"
            value={overview?.engagement?.unique_countries || 0}
            subtitle="Unique destinations"
            icon={Globe}
            color="accent"
          />
          <StatCard
            title="Premium Users"
            value={overview?.users?.premium || 0}
            subtitle={`${overview?.users?.total ? Math.round((overview.users.premium / overview.users.total) * 100) : 0}% conversion`}
            icon={Crown}
            color="yellow-500"
          />
          <StatCard
            title="Founding Members"
            value={overview?.users?.founding_members || 0}
            subtitle="Lifetime premium"
            icon={Crown}
            color="amber-500"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Partner Links"
            value={overview?.users?.with_partners || 0}
            subtitle="Couples using together"
            icon={Heart}
            color="coral"
          />
          <StatCard
            title="Past Trips Logged"
            value={overview?.items?.past_trips_logged || 0}
            subtitle="Pre-app trips added"
            icon={Calendar}
            color="secondary"
          />
          <StatCard
            title="Achievements Unlocked"
            value={overview?.engagement?.total_achievements || 0}
            subtitle="Badges earned"
            icon={Trophy}
            color="yellow-500"
          />
          <StatCard
            title="Groups Created"
            value={overview?.engagement?.total_groups || 0}
            subtitle="Travel groups"
            icon={Users}
            color="primary"
          />
        </div>

        {/* Detailed Analytics Tabs */}
        <Tabs defaultValue="growth" className="space-y-4">
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="bg-card border border-border inline-flex min-w-max">
              <TabsTrigger value="growth">User Growth</TabsTrigger>
              <TabsTrigger value="live-ops" className="text-emerald-400">
                <Activity className="w-4 h-4 mr-1" />
                Live Ops
              </TabsTrigger>
              <TabsTrigger value="reliability" className="text-orange-400">
                <ServerCrash className="w-4 h-4 mr-1" />
                Reliability
              </TabsTrigger>
              <TabsTrigger value="feature-flags" className="text-pink-400">
                <FlaskConical className="w-4 h-4 mr-1" />
                Feature Flags
              </TabsTrigger>
              <TabsTrigger value="ai-insights" className="text-violet-400">
                <Brain className="w-4 h-4 mr-1" />
                AI Insights
              </TabsTrigger>
              <TabsTrigger value="monetization" onClick={fetchMonetizationData}>
                <DollarSign className="w-4 h-4 mr-1" />
                Monetization
              </TabsTrigger>
              <TabsTrigger value="growth-loops" onClick={fetchGrowthData}>
                <Share2 className="w-4 h-4 mr-1" />
                Growth Loops
              </TabsTrigger>
              <TabsTrigger value="users" onClick={() => searchUsers()}>
                <UserCog className="w-4 h-4 mr-1" />
                User Management
              </TabsTrigger>
              <TabsTrigger value="engagement">Engagement</TabsTrigger>
              <TabsTrigger value="funnel">Funnel</TabsTrigger>
              <TabsTrigger value="destinations">Destinations</TabsTrigger>
              <TabsTrigger value="events">Events</TabsTrigger>
              <TabsTrigger value="emails">Emails</TabsTrigger>
              <TabsTrigger value="settings">
                <Shield className="w-4 h-4 mr-1" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="user-data">
                <Wrench className="w-4 h-4 mr-1" />
                User Data
              </TabsTrigger>
              <TabsTrigger value="cleanup" className="text-red-500">Cleanup</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="live-ops">
            <AdminLiveOpsTab />
          </TabsContent>

          <TabsContent value="reliability">
            <AdminReliabilityTab />
          </TabsContent>

          <TabsContent value="feature-flags">
            <AdminFeatureFlagsTab />
          </TabsContent>

          <TabsContent value="ai-insights">
            <AdminAIInsightsTab />
          </TabsContent>

          <TabsContent value="growth">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Daily Signups
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {userAnalytics?.daily_signups && (
                    <div className="h-48 flex items-end gap-1 px-2">
                      {(() => {
                        const visibleDays = userAnalytics.daily_signups.slice(-14);
                        const maxSignups = Math.max(...visibleDays.map(d => d.signups), 1);
                        return visibleDays.map((day, idx) => {
                          const barHeight = day.signups > 0 
                            ? Math.max((day.signups / maxSignups) * 100, 25) 
                            : 5;
                          return (
                            <div key={day.date} className="flex-1 flex flex-col items-center justify-end h-full min-w-[12px]">
                              <div 
                                className={`w-full rounded-t transition-all ${day.signups > 0 ? 'bg-primary hover:bg-primary/80' : 'bg-muted-foreground/30'}`}
                                style={{ 
                                  height: `${barHeight}%`,
                                  minHeight: day.signups > 0 ? '24px' : '6px'
                                }}
                                title={`${day.date}: ${day.signups} signup${day.signups !== 1 ? 's' : ''}`}
                              />
                              {idx % 2 === 0 && (
                                <span className="text-[9px] text-muted-foreground mt-1 -rotate-45 whitespace-nowrap">
                                  {day.date.slice(5)}
                                </span>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-secondary" />
                    User Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="text-sm text-muted-foreground">New users today</span>
                    <span className="font-bold text-foreground">{overview?.users?.today || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="text-sm text-muted-foreground">New users this week</span>
                    <span className="font-bold text-foreground">{overview?.users?.this_week || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="text-sm text-muted-foreground">New users this month</span>
                    <span className="font-bold text-foreground">{overview?.users?.this_month || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
                    <span className="text-sm text-muted-foreground">Active users (7 days)</span>
                    <span className="font-bold text-primary">{eventAnalytics?.active_users_last_7_days || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Monetization Tab */}
          <TabsContent value="monetization">
            {monetizationLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : monetizationOverview ? (
              <div className="space-y-6">
                {/* Core KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Total MRR</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">
                      £{monetizationOverview.mrr?.total?.toFixed(0) || 0}
                    </p>
                  </Card>
                  
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-4 h-4 text-primary" />
                      <span className="text-xs font-medium text-muted-foreground">Explorer Pro</span>
                    </div>
                    <p className="text-xl font-bold">{monetizationOverview.kpis?.explorer_pro_count || 0}</p>
                    <p className="text-xs text-muted-foreground">£{monetizationOverview.mrr?.explorer_pro?.toFixed(0)}/mo</p>
                  </Card>
                  
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-secondary" />
                      <span className="text-xs font-medium text-muted-foreground">Duo</span>
                    </div>
                    <p className="text-xl font-bold">{monetizationOverview.kpis?.duo_count || 0}</p>
                    <p className="text-xs text-muted-foreground">£{monetizationOverview.mrr?.duo?.toFixed(0)}/mo</p>
                  </Card>
                  
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-medium text-muted-foreground">Active Trials</span>
                    </div>
                    <p className="text-xl font-bold">{monetizationOverview.kpis?.active_trials || 0}</p>
                    <p className="text-xs text-muted-foreground">{monetizationOverview.kpis?.trial_starts || 0} started</p>
                  </Card>
                  
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      <span className="text-xs font-medium text-muted-foreground">Annual %</span>
                    </div>
                    <p className="text-xl font-bold">{monetizationOverview.rates?.annual_conversion || 0}%</p>
                  </Card>
                  
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Percent className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-medium text-muted-foreground">Duo Attach</span>
                    </div>
                    <p className="text-xl font-bold">{monetizationOverview.rates?.duo_attach_rate || 0}%</p>
                  </Card>
                </div>

                {/* Conversion Funnel & Rates */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Funnel Visualization */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" />
                        Conversion Funnel
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {[
                          { label: "Paywall Views", value: monetizationOverview.funnel?.paywall_impressions, color: "bg-blue-500" },
                          { label: "CTA Taps", value: monetizationOverview.funnel?.cta_taps, color: "bg-indigo-500" },
                          { label: "Trial Starts", value: monetizationOverview.funnel?.trial_starts, color: "bg-purple-500" },
                          { label: "Conversions", value: monetizationOverview.funnel?.conversions, color: "bg-emerald-500" },
                        ].map((step, idx) => {
                          const maxVal = monetizationOverview.funnel?.paywall_impressions || 1;
                          const width = Math.max((step.value / maxVal) * 100, 5);
                          return (
                            <div key={idx} className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span>{step.label}</span>
                                <span className="font-semibold">{step.value || 0}</span>
                              </div>
                              <div className="h-6 bg-accent rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${step.color} rounded-full transition-all`}
                                  style={{ width: `${width}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Conversion Rates */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Percent className="w-5 h-5 text-primary" />
                        Key Rates
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { label: "Trial → Paid", value: monetizationOverview.rates?.trial_to_paid, target: 30 },
                          { label: "Paywall → CTA", value: monetizationOverview.rates?.paywall_to_cta, target: 20 },
                          { label: "CTA → Purchase", value: monetizationOverview.rates?.cta_to_purchase, target: 15 },
                          { label: "Cancel Save", value: monetizationOverview.rates?.cancel_save_rate, target: 25 },
                        ].map((rate, idx) => (
                          <div key={idx} className="p-3 bg-accent/30 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1">{rate.label}</p>
                            <p className={`text-2xl font-bold ${(rate.value || 0) >= rate.target ? "text-emerald-600" : "text-foreground"}`}>
                              {rate.value || 0}%
                            </p>
                            <p className="text-xs text-muted-foreground">Target: {rate.target}%</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Paywall Performance */}
                {paywallPerformance && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary" />
                        Paywall Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-2 font-medium">Paywall</th>
                              <th className="text-right py-2 px-2 font-medium">Views</th>
                              <th className="text-right py-2 px-2 font-medium">Dismiss %</th>
                              <th className="text-right py-2 px-2 font-medium">CTA %</th>
                              <th className="text-right py-2 px-2 font-medium">Trials</th>
                              <th className="text-right py-2 px-2 font-medium">Conv %</th>
                              <th className="text-left py-2 px-2 font-medium">Best Trigger</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(paywallPerformance.paywalls || {}).map(([type, data]) => (
                              <tr key={type} className="border-b border-border/50 hover:bg-accent/20">
                                <td className="py-2 px-2 font-medium capitalize">{type.replace("_", " ")}</td>
                                <td className="text-right py-2 px-2">{data.impressions}</td>
                                <td className="text-right py-2 px-2">
                                  <span className={data.dismiss_rate > 70 ? "text-red-500" : "text-foreground"}>
                                    {data.dismiss_rate}%
                                  </span>
                                </td>
                                <td className="text-right py-2 px-2">
                                  <span className={data.cta_rate > 20 ? "text-emerald-600" : "text-foreground"}>
                                    {data.cta_rate}%
                                  </span>
                                </td>
                                <td className="text-right py-2 px-2">{data.trial_starts}</td>
                                <td className="text-right py-2 px-2">
                                  <span className={data.conversion_rate > 5 ? "text-emerald-600 font-semibold" : "text-foreground"}>
                                    {data.conversion_rate}%
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-xs text-muted-foreground">{data.top_trigger || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* A/B Experiments */}
                {experimentResults && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FlaskConical className="w-5 h-5 text-primary" />
                        A/B Experiments
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(experimentResults.experiments || {}).map(([name, exp]) => (
                          <div key={name} className="p-4 bg-accent/20 rounded-xl border border-border">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-sm capitalize">{name.replace(/_/g, " ")}</h4>
                              {exp.winner && (
                                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs rounded-full">
                                  Winner: {exp.winner}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">{exp.description}</p>
                            <div className="space-y-2">
                              {(exp.variants || []).slice(0, 3).map((v, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs">
                                  <span className={v._id === exp.winner ? "font-semibold text-emerald-600" : "text-muted-foreground"}>
                                    {v._id || `Variant ${idx + 1}`}
                                  </span>
                                  <span className="font-mono">
                                    {(v.conversion_rate || 0).toFixed(1)}%
                                    {v._id === exp.winner && exp.lift_percent > 0 && (
                                      <span className="text-emerald-600 ml-1">↑{exp.lift_percent}%</span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 pt-2 border-t border-border/50 flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {exp.sample_size} users
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {exp.confidence}% confidence
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Daily Revenue & Top Triggers */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Daily Revenue Chart */}
                  {dailyRevenue && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-emerald-500" />
                          Daily Revenue Trend
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-48 flex items-end gap-1">
                          {(dailyRevenue.daily_revenue || []).slice(-14).map((day, idx) => {
                            const maxRev = Math.max(...dailyRevenue.daily_revenue.map(d => d.revenue), 1);
                            const height = Math.max((day.revenue / maxRev) * 100, 5);
                            return (
                              <div key={day.date} className="flex-1 flex flex-col items-center">
                                <span className="text-[10px] text-muted-foreground mb-1">
                                  {day.revenue > 0 ? `£${day.revenue}` : ""}
                                </span>
                                <div 
                                  className="w-full bg-emerald-500/80 rounded-t transition-all hover:bg-emerald-500"
                                  style={{ height: `${height}%`, minHeight: '4px' }}
                                  title={`${day.date}: £${day.revenue}`}
                                />
                                {idx % 3 === 0 && (
                                  <span className="text-[9px] text-muted-foreground mt-1">
                                    {day.date.slice(5)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Top Converting Triggers */}
                  {topTriggers && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ArrowUpRight className="w-5 h-5 text-primary" />
                          Top Converting Triggers
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {(topTriggers.top_triggers || []).slice(0, 6).map((trigger, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx === 0 ? "bg-amber-500 text-white" :
                                  idx === 1 ? "bg-gray-400 text-white" :
                                  idx === 2 ? "bg-amber-700 text-white" :
                                  "bg-accent text-muted-foreground"
                                }`}>
                                  {idx + 1}
                                </span>
                                <span className="text-sm capitalize">{(trigger._id || "Unknown").replace(/_/g, " ")}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-semibold">{trigger.conversions}</span>
                                <span className="text-xs text-muted-foreground ml-1">conv</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Variant Leaderboard */}
                {variantLeaderboard && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-500" />
                        Copy Variant Leaderboard
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {(variantLeaderboard.variants || []).slice(0, 4).map((v, idx) => (
                          <div key={idx} className={`p-4 rounded-xl border ${
                            idx === 0 ? "bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-300" :
                            "bg-accent/20 border-border"
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              {idx === 0 && <Crown className="w-4 h-4 text-amber-500" />}
                              <span className="font-semibold text-sm capitalize">{v._id || "Unknown"}</span>
                            </div>
                            <p className="text-2xl font-bold">
                              {(v.conversion_rate || 0).toFixed(1)}%
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {v.conversions} of {v.impressions} views
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Cancellation Save Stats */}
                {monetizationOverview.cancellation && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="w-5 h-5 text-primary" />
                        Cancellation Save Flow
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-accent/30 rounded-lg text-center">
                          <p className="text-3xl font-bold">{monetizationOverview.cancellation.cancel_flows}</p>
                          <p className="text-xs text-muted-foreground">Cancel Flows Started</p>
                        </div>
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                          <p className="text-3xl font-bold text-emerald-600">{monetizationOverview.cancellation.save_accepted}</p>
                          <p className="text-xs text-muted-foreground">Saves Accepted</p>
                        </div>
                        <div className="p-4 bg-accent/30 rounded-lg text-center">
                          <p className="text-3xl font-bold">
                            {monetizationOverview.cancellation.cancel_flows > 0 
                              ? Math.round((monetizationOverview.cancellation.save_accepted / monetizationOverview.cancellation.cancel_flows) * 100)
                              : 0}%
                          </p>
                          <p className="text-xs text-muted-foreground">Save Rate</p>
                        </div>
                        <div className="p-4 bg-accent/30 rounded-lg text-center">
                          <p className="text-3xl font-bold text-emerald-600">
                            £{(monetizationOverview.cancellation.save_accepted * 5).toFixed(0)}
                          </p>
                          <p className="text-xs text-muted-foreground">Est. Revenue Retained</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Click to load monetization data</p>
                <Button onClick={fetchMonetizationData} className="mt-4">
                  Load Data
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Growth Loops Tab */}
          <TabsContent value="growth-loops">
            {growthLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : growthOverview ? (
              <div className="space-y-6">
                {/* Referral Section */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                    <Share2 className="w-5 h-5 text-primary" />
                    Referral System
                  </h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Viral Coefficient</div>
                        <div className="text-2xl font-bold text-primary">
                          {growthOverview.referrals?.viral_coefficient?.toFixed(3) || "0.000"}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Target: &ge;1.0
                        </div>
                        <div className={`mt-2 px-2 py-1 rounded text-xs inline-block ${
                          (growthOverview.referrals?.viral_coefficient || 0) >= 0.5 
                            ? 'bg-green-500/20 text-green-600' 
                            : 'bg-amber-500/20 text-amber-600'
                        }`}>
                          {(growthOverview.referrals?.viral_coefficient || 0) >= 0.5 ? 'Healthy' : 'Needs Work'}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Referral Revenue</div>
                        <div className="text-2xl font-bold text-green-600">
                          £{growthOverview.referrals?.referral_revenue?.toFixed(2) || "0.00"}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          From referred users
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Referrals Activated</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.referrals?.referral_stats?.activated || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Sent: {growthOverview.referrals?.referral_stats?.sent || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Ambassadors</div>
                        <div className="text-2xl font-bold text-amber-500">
                          {growthOverview.referrals?.total_ambassadors || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Users with 25+ referrals
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Referral Funnel */}
                  <Card className="mt-4">
                    <CardHeader>
                      <CardTitle className="text-base">Referral Funnel</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {[
                          { label: "Sent", value: growthOverview.referrals?.referral_stats?.sent || 0, color: "bg-blue-500" },
                          { label: "Clicked", value: growthOverview.referrals?.referral_stats?.clicked || 0, color: "bg-indigo-500" },
                          { label: "Accepted", value: growthOverview.referrals?.referral_stats?.accepted || 0, color: "bg-purple-500" },
                          { label: "Activated", value: growthOverview.referrals?.referral_stats?.activated || 0, color: "bg-primary" },
                          { label: "Upgraded", value: growthOverview.referrals?.referral_stats?.upgraded || 0, color: "bg-green-500" }
                        ].map((stage, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="w-24 text-sm text-muted-foreground">{stage.label}</div>
                            <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${stage.color} transition-all`}
                                style={{ 
                                  width: `${Math.max(5, (stage.value / Math.max(growthOverview.referrals?.referral_stats?.sent || 1, 1)) * 100)}%` 
                                }}
                              />
                            </div>
                            <div className="w-12 text-sm font-medium text-right">{stage.value}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Streak Protection Section */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                    <Flame className="w-5 h-5 text-orange-500" />
                    Streak Protection
                  </h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Streak Saves</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.streaks?.streak_saves || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Freezes used
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Upgrades from Streak</div>
                        <div className="text-2xl font-bold text-green-600">
                          {growthOverview.streaks?.upgrades_from_streak_trigger || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Fear-driven conversions
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Save Flow Rate</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.streaks?.save_rate_percent?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Shown: {growthOverview.streaks?.save_flow_shown || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Milestone Claims</div>
                        <div className="text-2xl font-bold text-amber-500">
                          {growthOverview.streaks?.milestone_claims || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Badges earned
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Streakers */}
                  {growthOverview.streaks?.top_streakers?.length > 0 && (
                    <Card className="mt-4">
                      <CardHeader>
                        <CardTitle className="text-base">Top Streakers</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {growthOverview.streaks.top_streakers.slice(0, 5).map((user, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                              <div className="flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx === 0 ? 'bg-amber-500 text-white' : 
                                  idx === 1 ? 'bg-gray-400 text-white' : 
                                  idx === 2 ? 'bg-amber-700 text-white' : 'bg-muted text-muted-foreground'
                                }`}>{idx + 1}</span>
                                <span className="font-medium">{user.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Flame className="w-4 h-4 text-orange-500" />
                                <span className="font-bold">{user.current_streak || 0}</span>
                                <span className="text-xs text-muted-foreground">
                                  (best: {user.longest_streak || 0})
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Renewal & Win-back Section */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                    <RotateCcw className="w-5 h-5 text-blue-500" />
                    Renewal & Win-back
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Win-back Conversion</div>
                        <div className="text-2xl font-bold text-green-600">
                          {growthOverview.renewals?.win_back?.conversion_rate?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {growthOverview.renewals?.win_back?.conversions || 0} / {growthOverview.renewals?.win_back?.campaigns_shown || 0} campaigns
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Trial Conversion</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.renewals?.trials?.conversion_rate?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {growthOverview.renewals?.trials?.converted || 0} / {growthOverview.renewals?.trials?.started || 0} trials
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Renewal Rate</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.renewals?.renewals?.rate?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {growthOverview.renewals?.renewals?.renewed || 0} renewed, {growthOverview.renewals?.renewals?.expired || 0} expired
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Nudge Engagement</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.renewals?.nudges?.engagement_rate?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {growthOverview.renewals?.nudges?.shown || 0} nudges shown
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Churn Risk Distribution */}
                  <Card className="mt-4">
                    <CardHeader>
                      <CardTitle className="text-base">Churn Risk Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4">
                        {Object.entries(growthOverview.renewals?.churn_risk_distribution || {}).map(([level, count]) => (
                          <div key={level} className="text-center p-3 rounded-lg bg-muted/50">
                            <div className={`text-2xl font-bold ${
                              level === 'critical' ? 'text-red-500' :
                              level === 'high' ? 'text-orange-500' :
                              level === 'medium' ? 'text-amber-500' : 'text-green-500'
                            }`}>{count}</div>
                            <div className="text-xs text-muted-foreground capitalize">{level}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Rules Engine Section */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Automation Rules
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Total Triggers</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.rules?.total_triggers || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Rules fired
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Notifications Sent</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.rules?.notifications?.sent || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {growthOverview.rules?.notifications?.read || 0} read
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Engagement Rate</div>
                        <div className="text-2xl font-bold">
                          {growthOverview.rules?.notifications?.engagement_rate?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Notifications opened
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Rules */}
                  {growthOverview.rules?.top_rules?.length > 0 && (
                    <Card className="mt-4">
                      <CardHeader>
                        <CardTitle className="text-base">Top Performing Rules</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {growthOverview.rules.top_rules.map((rule, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                              <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-yellow-500" />
                                <span className="font-medium text-sm">{rule.rule_id.replace(/_/g, ' ')}</span>
                              </div>
                              <span className="font-bold">{rule.triggers} triggers</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Share2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Click to load growth analytics</p>
                <Button onClick={fetchGrowthData} className="mt-4">
                  Load Data
                </Button>
              </div>
            )}
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* User Search Panel */}
              <div className="lg:col-span-1 space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Search className="w-5 h-5 text-primary" />
                        Search Users
                      </CardTitle>
                      <Button
                        size="sm"
                        onClick={() => setShowCreateUserModal(true)}
                        data-testid="create-user-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Create
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search by name or email..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && searchUsers()}
                        data-testid="admin-user-search"
                      />
                      <Button onClick={searchUsers} disabled={userListLoading}>
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <Select value={userFilter} onValueChange={(v) => { setUserFilter(v); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="premium">Premium Only</SelectItem>
                        <SelectItem value="founding">Founding Members</SelectItem>
                        <SelectItem value="partnered">Has Partner</SelectItem>
                        <SelectItem value="grouped">In Groups</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button variant="outline" className="w-full" onClick={searchUsers}>
                      Apply Filter
                    </Button>
                  </CardContent>
                </Card>

                {/* User List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Users ({userList.length}{userListTotal > userList.length ? ` of ${userListTotal}` : ""})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {userListLoading ? (
                      <div className="text-center py-8 text-muted-foreground">Loading...</div>
                    ) : userList.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        Click search to load users
                      </div>
                    ) : (
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {userList.map((user) => (
                          <div
                            key={user.id}
                            className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                              selectedUser === user.id 
                                ? "bg-primary/10 border-primary" 
                                : "hover:bg-muted border-transparent"
                            }`}
                            onClick={() => loadUserDetails(user.id)}
                            data-testid={`user-row-${user.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-foreground truncate flex items-center gap-1">
                                  {user.name}
                                  {user.is_admin && <ShieldCheck className="w-3 h-3 text-blue-500" title="Admin" />}
                                  {user.founding_explorer && <Crown className="w-3 h-3 text-amber-500" />}
                                  {user.is_premium && <CreditCard className="w-3 h-3 text-purple-500" />}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-7 px-2 ${user.is_admin ? 'text-blue-600 hover:text-blue-700' : 'text-muted-foreground hover:text-foreground'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleAdmin(user.id, user.is_admin);
                                  }}
                                  disabled={togglingAdmin === user.id}
                                  title={user.is_admin ? "Remove Admin" : "Make Admin"}
                                  data-testid={`toggle-admin-${user.id}`}
                                >
                                  {togglingAdmin === user.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : user.is_admin ? (
                                    <ShieldOff className="w-3 h-3" />
                                  ) : (
                                    <ShieldCheck className="w-3 h-3" />
                                  )}
                                </Button>
                                <div className="text-right text-xs text-muted-foreground">
                                  <div>{user.item_count} items</div>
                                  <div className="text-green-600">{user.completed_count} done</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {!userListLoading && userList.length > 0 && userList.length < userListTotal && (
                      <div className="pt-3 text-center border-t border-border mt-2">
                        <Button
                          variant="outline"
                          onClick={loadMoreUsers}
                          data-testid="admin-users-load-more"
                        >
                          Load more ({userListTotal - userList.length} remaining)
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* User Details Panel */}
              <div className="lg:col-span-2">
                {userDetailsLoading ? (
                  <Card>
                    <CardContent className="py-16 text-center text-muted-foreground">
                      Loading user details...
                    </CardContent>
                  </Card>
                ) : userDetails ? (
                  <div className="space-y-4">
                    {/* User Header */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-xl flex items-center gap-2">
                              {userDetails.user.name}
                              {userDetails.user.is_admin && (
                                <span className="text-blue-600 flex items-center gap-1 text-sm bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">
                                  <ShieldCheck className="w-3 h-3" />
                                  Admin
                                </span>
                              )}
                              {userDetails.user.founding_explorer && (
                                <span className="text-amber-500 flex items-center gap-1 text-sm">
                                  <Crown className="w-4 h-4" />
                                  #{userDetails.user.founding_explorer_number}
                                </span>
                              )}
                            </CardTitle>
                            <p className="text-muted-foreground">{userDetails.user.email}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              ID: {userDetails.user.id}
                            </p>
                          </div>
                          <div className="flex gap-2 flex-wrap justify-end">
                            <Button 
                              variant={userDetails.user.is_admin ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleToggleAdmin(userDetails.user.id, userDetails.user.is_admin)}
                              disabled={togglingAdmin === userDetails.user.id}
                              className={userDetails.user.is_admin ? "bg-blue-600 hover:bg-blue-700" : ""}
                              data-testid="toggle-admin-detail"
                            >
                              {togglingAdmin === userDetails.user.id ? (
                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                              ) : userDetails.user.is_admin ? (
                                <ShieldOff className="w-4 h-4 mr-1" />
                              ) : (
                                <ShieldCheck className="w-4 h-4 mr-1" />
                              )}
                              {userDetails.user.is_admin ? "Remove Admin" : "Make Admin"}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setEditingUser(!editingUser)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              {editingUser ? "Cancel" : "Edit"}
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={deleteUserAccount}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      
                      {editingUser && (
                        <CardContent className="border-t pt-4 space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label>Name</Label>
                              <Input
                                value={userEditForm.name || ""}
                                onChange={(e) => setUserEditForm({...userEditForm, name: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label>Admin Note</Label>
                              <Input
                                value={userEditForm.admin_note || ""}
                                onChange={(e) => setUserEditForm({...userEditForm, admin_note: e.target.value})}
                                placeholder="Internal note..."
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-6">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userEditForm.founding_explorer || false}
                                onChange={(e) => setUserEditForm({...userEditForm, founding_explorer: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Founding Explorer</span>
                            </label>
                            {userEditForm.founding_explorer && (
                              <Input
                                type="number"
                                value={userEditForm.founding_explorer_number || ""}
                                onChange={(e) => setUserEditForm({...userEditForm, founding_explorer_number: parseInt(e.target.value) || ""})}
                                placeholder="#"
                                className="w-20"
                              />
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button onClick={saveUserChanges} disabled={savingUser}>
                              <Save className="w-4 h-4 mr-1" />
                              {savingUser ? "Saving..." : "Save Changes"}
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="p-4 text-center">
                        <div className="text-2xl font-bold text-primary">{userDetails.item_stats.total}</div>
                        <p className="text-xs text-muted-foreground">Total Items</p>
                      </Card>
                      <Card className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{userDetails.item_stats.completed}</div>
                        <p className="text-xs text-muted-foreground">Completed</p>
                      </Card>
                      <Card className="p-4 text-center">
                        <div className="text-2xl font-bold text-purple-600">{userDetails.achievements.length}</div>
                        <p className="text-xs text-muted-foreground">Achievements</p>
                      </Card>
                      <Card className="p-4 text-center">
                        <div className="text-2xl font-bold text-secondary">{userDetails.photo_count}</div>
                        <p className="text-xs text-muted-foreground">Photos</p>
                      </Card>
                    </div>

                    {/* Subscription Management */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <CreditCard className="w-5 h-5 text-purple-600" />
                          Subscription
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {userDetails.subscriptions.length > 0 ? (
                          <div className="space-y-3">
                            {userDetails.subscriptions.map((sub) => (
                              <div key={sub.id || sub.stripe_subscription_id || `sub-${sub.start_date}`} className={`p-3 rounded-lg border ${sub.status === "active" ? "bg-green-50 dark:bg-green-900/20 border-green-200" : "bg-muted"}`}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className={`font-medium ${sub.status === "active" ? "text-green-700 dark:text-green-400" : "text-muted-foreground"}`}>
                                      {sub.plan || "Unknown Plan"}
                                    </span>
                                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${sub.status === "active" ? "bg-green-200 text-green-800" : "bg-muted text-muted-foreground"}`}>
                                      {sub.status}
                                    </span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : "N/A"}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">No subscription history</p>
                        )}

                        <div className="flex gap-2 mt-4 pt-4 border-t">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => updateSubscription("active")}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                          >
                            Grant Premium
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => updateSubscription("canceled")}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            Revoke Premium
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Partner & Groups */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Heart className="w-5 h-5 text-secondary" />
                            Partner
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {userDetails.partner ? (
                            <div className="p-3 bg-secondary/10 rounded-lg">
                              <div className="font-medium text-foreground">{userDetails.partner.name}</div>
                              <div className="text-xs text-muted-foreground">{userDetails.partner.email}</div>
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-sm">No partner linked</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="w-5 h-5 text-primary" />
                            Groups ({userDetails.groups.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {userDetails.groups.length > 0 ? (
                            <div className="space-y-2">
                              {userDetails.groups.map((group) => (
                                <div key={group.id} className="p-2 bg-muted rounded text-sm">
                                  {group.name}
                                  {group.created_by === selectedUser && (
                                    <span className="ml-1 text-xs text-primary">(Owner)</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-sm">Not in any groups</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Recent Items */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ListTodo className="w-5 h-5 text-accent" />
                          Recent Items
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {userDetails.recent_items.length > 0 ? (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {userDetails.recent_items.map((item) => (
                              <div key={item.id} className="flex items-center justify-between p-2 bg-muted rounded">
                                <div className="flex items-center gap-2">
                                  {item.completed && <CheckCircle className="w-4 h-4 text-green-600" />}
                                  <span className="text-sm font-medium truncate max-w-xs">{item.title}</span>
                                </div>
                                <span className="text-xs text-muted-foreground">{item.category}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">No items yet</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-16 text-center text-muted-foreground">
                      <UserCog className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a user from the list to view details</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="engagement">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-secondary" />
                    Items by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart
                    data={itemAnalytics?.categories}
                    labelKey="category"
                    valueKey="count"
                    color="#81B29A"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Completion Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <div className="text-5xl font-bold text-primary mb-2">
                      {overview?.items?.completion_rate || 0}%
                    </div>
                    <p className="text-sm text-muted-foreground">Overall completion rate</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-3 bg-secondary/10 rounded-lg">
                      <div className="text-2xl font-bold text-secondary">{overview?.items?.completed || 0}</div>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="p-3 bg-accent/20 rounded-lg">
                      <div className="text-2xl font-bold text-foreground">
                        {(overview?.items?.total || 0) - (overview?.items?.completed || 0)}
                      </div>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="funnel">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Conversion Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FunnelChart data={funnelAnalytics?.funnel} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="destinations">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  Popular Destinations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-foreground mb-3">By Total Items</h4>
                    <SimpleBarChart
                      data={countryAnalytics?.popular_countries}
                      labelKey="name"
                      valueKey="total_items"
                      color="#E07A5F"
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Completed Visits</h4>
                    <SimpleBarChart
                      data={countryAnalytics?.popular_countries?.filter(c => c.completed > 0)}
                      labelKey="name"
                      valueKey="completed"
                      color="#81B29A"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Event Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart
                    data={eventAnalytics?.event_types}
                    labelKey="event"
                    valueKey="count"
                    color="#E07A5F"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-secondary" />
                    Page Views
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart
                    data={eventAnalytics?.page_views}
                    labelKey="page"
                    valueKey="views"
                    color="#81B29A"
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="emails">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Email Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Email Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <div className="text-2xl font-bold text-foreground">{emailStats?.total_emails || 0}</div>
                      <p className="text-xs text-muted-foreground">Total Sent</p>
                    </div>
                    <div className="p-4 bg-primary/10 rounded-lg text-center">
                      <div className="text-2xl font-bold text-primary">{emailStats?.emails_this_week || 0}</div>
                      <p className="text-xs text-muted-foreground">This Week</p>
                    </div>
                    <div className="p-4 bg-secondary/10 rounded-lg text-center">
                      <div className="text-2xl font-bold text-secondary">{emailStats?.emails_today || 0}</div>
                      <p className="text-xs text-muted-foreground">Today</p>
                    </div>
                    <div className="p-4 bg-accent/20 rounded-lg text-center">
                      <div className="text-2xl font-bold text-foreground">{emailStats?.pending_drip_emails || 0}</div>
                      <p className="text-xs text-muted-foreground">Pending Drip</p>
                    </div>
                  </div>

                  {emailStats?.by_type && Object.keys(emailStats.by_type).length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-foreground mb-2">By Type</h4>
                      <div className="space-y-2">
                        {Object.entries(emailStats.by_type).map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center p-2 bg-muted rounded">
                            <span className="text-sm text-muted-foreground capitalize">{type.replace(/_/g, ' ')}</span>
                            <span className="font-medium text-foreground">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Test Email */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Send className="w-5 h-5 text-secondary" />
                    Send Test Email
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="test@example.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Type</Label>
                    <Select value={testEmailType} onValueChange={setTestEmailType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="welcome">Welcome Email</SelectItem>
                        <SelectItem value="inactivity">Inactivity (7 days)</SelectItem>
                        <SelectItem value="feature_world_map">Feature: World Map</SelectItem>
                        <SelectItem value="feature_ai_suggestions">Feature: AI Suggestions</SelectItem>
                        <SelectItem value="feature_partner_linking">Feature: Partner Linking</SelectItem>
                        <SelectItem value="feature_achievements">Feature: Achievements</SelectItem>
                        <SelectItem value="feature_groups">Feature: Groups</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleSendTestEmail} 
                    disabled={sendingEmail}
                    className="w-full"
                  >
                    {sendingEmail ? "Sending..." : "Send Test Email"}
                  </Button>

                  <div className="border-t border-border pt-4 mt-4 space-y-3">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Automated Tasks
                    </h4>
                    <Button 
                      variant="outline" 
                      onClick={handleProcessQueue}
                      disabled={processingQueue}
                      className="w-full"
                    >
                      {processingQueue ? "Processing..." : "Process Email Queue (Drip Campaign)"}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleCheckInactive}
                      disabled={checkingInactive}
                      className="w-full"
                    >
                      {checkingInactive ? "Checking..." : "Check Inactive Users (7+ days)"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Email Types Info */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Email Types & Triggers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 border border-border rounded-lg">
                    <h4 className="font-medium text-foreground flex items-center gap-2 mb-2">
                      🎉 Welcome Email
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Sent immediately when a user signs up. Includes quick start guide and CTA.
                    </p>
                  </div>
                  <div className="p-4 border border-border rounded-lg">
                    <h4 className="font-medium text-foreground flex items-center gap-2 mb-2">
                      📚 Feature Education
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Drip campaign over 10 days: World Map → AI Suggestions → Partner Linking → Achievements → Groups
                    </p>
                  </div>
                  <div className="p-4 border border-border rounded-lg">
                    <h4 className="font-medium text-foreground flex items-center gap-2 mb-2">
                      👋 Re-engagement
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Sent to users inactive for 7+ days. Includes feedback request and feature highlights.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* Subscription Enforcement */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Subscription Enforcement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <h4 className="font-medium">Enforce Plan Limits</h4>
                      <p className="text-sm text-muted-foreground">
                        When enabled, users are restricted based on their subscription plan (Free, Explorer Pro, Duo).
                        Founding members always have full access regardless of this setting.
                      </p>
                      {enforcementUpdatedBy && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Last updated by: {enforcementUpdatedBy}
                        </p>
                      )}
                    </div>
                    <Button
                      variant={enforcementEnabled ? "default" : "outline"}
                      onClick={toggleEnforcement}
                      disabled={enforcementLoading}
                      className={enforcementEnabled ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      {enforcementLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : enforcementEnabled ? (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      ) : (
                        <X className="w-4 h-4 mr-2" />
                      )}
                      {enforcementEnabled ? "Enabled" : "Disabled"}
                    </Button>
                  </div>

                  <div className={`p-4 rounded-lg border ${enforcementEnabled ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      {enforcementEnabled ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Restrictions Active
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          All Features Unlocked
                        </>
                      )}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {enforcementEnabled 
                        ? "Free users get unlimited trips and 1 group, capped at 5 photos per trip and 20 memories total. Premium features (AI suggestions, Year in Review, unlimited groups, budget tracking, PDF export) require an upgrade."
                        : "All users currently have access to all features regardless of their plan. Enable enforcement to activate the freemium model."
                      }
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="p-4 bg-card border rounded-lg">
                      <h5 className="font-medium text-sm mb-2">Free Plan</h5>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• 10 bucket list items</li>
                        <li>• 5 photos per item</li>
                        <li>• 1 partner link</li>
                        <li>• Basic features only</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-card border rounded-lg border-primary/50">
                      <h5 className="font-medium text-sm mb-2">Explorer Pro - £5.05/mo</h5>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Unlimited items & photos</li>
                        <li>• AI suggestions</li>
                        <li>• Budget tracking</li>
                        <li>• PDF export</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-card border rounded-lg border-secondary/50">
                      <h5 className="font-medium text-sm mb-2">Duo - £7.58/mo</h5>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Everything in Pro</li>
                        <li>• Shared billing</li>
                        <li>• Trip collaboration</li>
                        <li>• Anniversary reminders</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* User Data Management Tab */}
          <TabsContent value="user-data">
            <AdminUserDataPanel />
          </TabsContent>

          {/* Cleanup Tab */}
          <TabsContent value="cleanup">
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="w-5 h-5" />
                  Database Cleanup - Remove Test Accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">Warning</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        This will permanently delete test accounts and all their data. Real users are protected.
                      </p>
                    </div>
                  </div>
                </div>

                {!cleanupPreview ? (
                  <Button 
                    onClick={handlePreviewCleanup} 
                    disabled={loadingCleanup}
                    variant="outline"
                    className="w-full"
                  >
                    {loadingCleanup ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    Load All Users
                  </Button>
                ) : (
                  <div className="space-y-4">
                    {/* Selection Controls */}
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={selectAllTestAccounts}>
                        Select All Test Accounts
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAll}>
                        Deselect All
                      </Button>
                      <span className="text-sm text-muted-foreground self-center ml-auto">
                        {selectedForDeletion.size} selected for deletion
                      </span>
                    </div>

                    {/* All Users List */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted px-4 py-2 font-medium text-sm border-b">
                        All Users ({cleanupPreview.real_accounts_count + cleanupPreview.test_accounts_count})
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {/* Real Users — Protected (per-row toggle to release) */}
                        {cleanupPreview.real_accounts.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center gap-3 px-4 py-2 border-b bg-green-50 dark:bg-green-900/20"
                            data-testid={`cleanup-row-${user.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={false}
                              disabled
                              className="w-4 h-4 opacity-50"
                              title="Protected — un-toggle the Protect switch on the right to allow deletion"
                            />
                            <Shield className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">
                                {user.name}
                                {user.founding_explorer && <Crown className="w-3 h-3 text-amber-500 inline ml-1" />}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                            </div>
                            <span className="text-[10px] text-green-600 font-medium uppercase tracking-wide hidden sm:inline">
                              {user.protection_source === "legacy" ? "Legacy" : "Protected"}
                            </span>
                            <label
                              className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                              title="Turn off to allow deletion"
                              data-testid={`protect-toggle-${user.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={(e) => toggleProtection(user, e.target.checked)}
                                onClick={(ev) => ev.stopPropagation()}
                                className="w-4 h-4 accent-green-600"
                              />
                              <span className="text-green-700 dark:text-green-400 font-medium">Protect</span>
                            </label>
                          </div>
                        ))}

                        {/* Test/Other Users — Selectable. Each row has a Protect
                            toggle to MARK the user as a real protected account
                            (moves them up to the green section). */}
                        {cleanupPreview.test_accounts.map((user) => (
                          <div
                            key={user.id}
                            className={`flex items-center gap-3 px-4 py-2 border-b cursor-pointer hover:bg-muted/50 ${
                              selectedForDeletion.has(user.id) ? 'bg-red-50 dark:bg-red-900/20' : ''
                            }`}
                            onClick={() => toggleUserSelection(user.id)}
                            data-testid={`cleanup-row-${user.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedForDeletion.has(user.id)}
                              onChange={() => toggleUserSelection(user.id)}
                              className="w-4 h-4 accent-red-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">
                                {user.name}
                                {user.founding_explorer && <Crown className="w-3 h-3 text-amber-500 inline ml-1" />}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                            </div>
                            <span className="text-xs text-muted-foreground hidden sm:inline">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                            </span>
                            <label
                              className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                              onClick={(ev) => ev.stopPropagation()}
                              title="Turn on to mark this account as protected (cannot be deleted until un-toggled)"
                              data-testid={`protect-toggle-${user.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={(e) => toggleProtection(user, e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-muted-foreground">Protect</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => {setCleanupPreview(null); setSelectedForDeletion(new Set());}} 
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleExecuteCleanup} 
                        disabled={executingCleanup || selectedForDeletion.size === 0}
                        variant="destructive"
                        className="flex-1"
                      >
                        {executingCleanup ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Delete {selectedForDeletion.size} Selected
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Feedback Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-secondary" />
              Feedback Board Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-foreground">{overview?.engagement?.total_feedback || 0}</div>
                <p className="text-xs text-muted-foreground">Total Requests</p>
              </div>
              <div className="text-center p-4 bg-accent/20 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{overview?.engagement?.pending_feedback || 0}</div>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {(overview?.engagement?.total_feedback || 0) - (overview?.engagement?.pending_feedback || 0)}
                </div>
                <p className="text-xs text-muted-foreground">Reviewed</p>
              </div>
              <div className="text-center">
                <Button 
                  variant="outline" 
                  className="w-full h-full min-h-[80px]"
                  onClick={() => navigate('/feedback')}
                >
                  View Feedback Board
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create User Modal */}
      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Create New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account manually. The user will receive their credentials from you.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Name *</Label>
              <Input
                id="create-name"
                placeholder="John Doe"
                value={createUserForm.name}
                onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})}
                data-testid="create-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-email">Email *</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="john@example.com"
                value={createUserForm.email}
                onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                data-testid="create-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-password">Password *</Label>
              <Input
                id="create-password"
                type="password"
                placeholder="Min 6 characters"
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})}
                data-testid="create-user-password"
              />
            </div>
            
            <div className="flex flex-col gap-3 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createUserForm.is_admin}
                  onChange={(e) => setCreateUserForm({...createUserForm, is_admin: e.target.checked})}
                  className="w-4 h-4"
                  data-testid="create-user-admin"
                />
                <span className="text-sm flex items-center gap-1">
                  <ShieldCheck className="w-4 h-4 text-blue-500" />
                  Make Admin
                </span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createUserForm.founding_explorer}
                  onChange={(e) => setCreateUserForm({...createUserForm, founding_explorer: e.target.checked})}
                  className="w-4 h-4"
                  data-testid="create-user-founder"
                />
                <span className="text-sm flex items-center gap-1">
                  <Crown className="w-4 h-4 text-amber-500" />
                  Founding Explorer
                </span>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCreateUserModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={creatingUser}
              data-testid="create-user-submit"
            >
              {creatingUser ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
